import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, IconButton } from '@upmc/ims.component-library';
import { addMessage } from '../../../store/ancillary/actions';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Icon from '../MDC/Icon/Icon';
import StyleAuxiliary from '../../../hoc/Auxiliary/StyleAuxiliary/StyleAuxiliary';
import PropTypes from 'prop-types';
import { CSS_CLASSES } from './_constants';

import classes from './Survey.module.scss';

/**
 * Fuctional component for prompting users to complete survey in both intrusive and unobtrusive manners.
 * Intrusive prompts via dialog, unobtrusive via message banner.
 * Visibility of component toggled with `props.active`.
 * @param {Object} props Properties passed to component.
 * @returns {ReactElement} Rendered component.
 */
const Survey = props => {
	const [sIsDialogOpen, setIsDialogOpen] = useState(props.intrusive);
	const [sWasDialogAccepted, setWasDialogAccepted] = useState(false);

	const dispatch = useDispatch();

	const SURVEY_QUESTION = useMemo(
		() =>
			'Will you fill out a brief survey to provide feedback on your ' +
			props.target +
			' experience?',
		[props.target]
	);

	/**
	 * Effect on changes to @see props.active. Dispatches a banner message if instance active and non-obtrusive.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		if (props.active && !props.intrusive) {
			dispatch(
				addMessage({
					Key: 0,
					Value: (
						<>
							{SURVEY_QUESTION} If so, click{' '}
							<a
								className={classes[CSS_CLASSES.LINK]}
								href={props.href}
								target='_blank'
								rel='noopener noreferrer'
							>
								here
							</a>
							.
						</>
					),
				})
			);
		}
	}, [dispatch, props.active, props.href, props.intrusive, SURVEY_QUESTION]);

	const onCloseHandler = action => {
		let wasAccepted = false;
		setIsDialogOpen(false);
		switch (String(action).toLowerCase()) {
			case 'accept':
				wasAccepted = true;
				break;
			case 'dismiss':
				wasAccepted = false;
				break;
			default:
				wasAccepted = false;
				break;
		}
		setWasDialogAccepted(wasAccepted);
	};

	const dialog = props.intrusive ? (
		<Dialog onClose={() => onCloseHandler()} open={sIsDialogOpen}>
			<DialogTitle>Survey Participation</DialogTitle>
			<DialogContent>{SURVEY_QUESTION}</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					onClick={() => onCloseHandler('dismiss')}
				>
					No
				</Button>
				<Button variant='text' onClick={() => onCloseHandler('accept')}>
					Yes
				</Button>
			</DialogActions>
		</Dialog>
	) : null;
	const surveyIntrusive =
		props.intrusive && sWasDialogAccepted ? props.children : null;
	const surveyIntrusiveContent = surveyIntrusive ? (
		<StyleAuxiliary classes={classes[CSS_CLASSES.CONTAINER]}>
			<StyleAuxiliary classes={classes[CSS_CLASSES.BACKDROP]}>
				<IconButton
					className={classes[CSS_CLASSES.ICON_BUTTON]}
					onClick={() => setWasDialogAccepted(false)}
				>
					<Icon icon='clear' />
				</IconButton>
				{surveyIntrusive}
			</StyleAuxiliary>
		</StyleAuxiliary>
	) : null;
	return props.active ? (
		<Fragment>
			{dialog}
			{surveyIntrusiveContent}
		</Fragment>
	) : null;
};

Survey.propTypes = {
	active: PropTypes.bool,
	href: PropTypes.string,
	intrusive: PropTypes.bool,
	target: PropTypes.string.isRequired,
};

export default Survey;
